import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import type { FetchResponse, MaybeOptionalInit } from "openapi-fetch";
import type { HttpMethod, MediaType, PathsWithMethod } from "openapi-typescript-helpers";
import type { paths } from "../generated/schema";
import { getReturnWidgetFetchClient } from "./return-widget-client";

type MutMethod = "post" | "put" | "patch" | "delete";

type InitFor<
	M extends HttpMethod,
	P extends PathsWithMethod<paths, M>,
> = MaybeOptionalInit<paths[P], M>;

type Operation<
	M extends HttpMethod,
	P extends PathsWithMethod<paths, M>,
> = paths[P][M] & Record<string, unknown>;

type DataFor<
	M extends HttpMethod,
	P extends PathsWithMethod<paths, M>,
> = Required<FetchResponse<Operation<M, P>, InitFor<M, P>, MediaType>>["data"];

type ErrorFor<
	M extends HttpMethod,
	P extends PathsWithMethod<paths, M>,
> = Required<FetchResponse<Operation<M, P>, InitFor<M, P>, MediaType>>["error"];

export interface UsePublicApiMutationOptions<TData, TVariables> {
	onSuccess?: (data: TData, variables: TVariables) => void;
	onError?: (error: unknown, variables: TVariables) => void;
	mapVariables?: (variables: TVariables) => unknown | Promise<unknown>;
}

export function usePublicApiMutation<
	Method extends MutMethod,
	Path extends PathsWithMethod<paths, Method>,
	TVariables = InitFor<Method, Path>,
>(
	method: Method,
	path: Path,
	options?: UsePublicApiMutationOptions<DataFor<Method, Path>, TVariables>,
): UseMutationResult<DataFor<Method, Path>, ErrorFor<Method, Path>, TVariables> {
	return useMutation({
		onSuccess: (data, variables) => options?.onSuccess?.(data, variables),
		onError: (error, variables) => options?.onError?.(error as unknown, variables),
		mutationFn: async (variables: TVariables) => {
			const init = options?.mapVariables
				? await options.mapVariables(variables)
				: variables;
			const uppercaseMethod = method.toUpperCase() as Uppercase<Method>;
			const client = getReturnWidgetFetchClient();
			const fetchFn = client[uppercaseMethod] as (
				url: Path,
				init: unknown,
			) => Promise<{ data?: unknown; error?: unknown }>;
			const { data, error } = await fetchFn(path, init);
			if (error) throw error;
			return data as DataFor<Method, Path>;
		},
	}) as UseMutationResult<DataFor<Method, Path>, ErrorFor<Method, Path>, TVariables>;
}
